import React, { useMemo } from 'react';
import AACTimg from 'src/images/carriers/AACT.gif';
import AVRTimg from 'src/images/carriers/AVRT.png';
import CCYQimg from 'src/images/carriers/CCYQ.png';
import DAYRimg from 'src/images/carriers/DAYR.webp';
import FEDXimg from 'src/images/carriers/FEDX.png';
import FWDNimg from 'src/images/carriers/FWDN.jpeg';
import FXFEimg from 'src/images/carriers/FXFE.png';
import HMESimg from 'src/images/carriers/HMES.jpeg';
import MANIimg from 'src/images/carriers/MANI.png';
import MIDWimg from 'src/images/carriers/MIDW.jpg';
import NPMEimg from 'src/images/carriers/NPME.gif';
import OAKHimg from 'src/images/carriers/OAKH.gif';
import ODFLimg from 'src/images/carriers/ODFL.png';
import RDWYimg from 'src/images/carriers/RDWY.png';
import RETLimg from 'src/images/carriers/RETL.png';
import SAIAimg from 'src/images/carriers/SAIA.jpeg';
import SEFLimg from 'src/images/carriers/SEFL.jpeg';
import SFPAimg from 'src/images/carriers/SFPA.jpg';
import SUONimg from 'src/images/carriers/SUON.png';
import TFORCEimg from 'src/images/carriers/TFORCE.jpeg';
import UPSimg from 'src/images/carriers/UPS.png';
import USPSimg from 'src/images/carriers/USPS.png';
import WARDimg from 'src/images/carriers/WARD.png';
import faviconImg from 'src/images/favicon.png';
import {
  Col,
  Input,
  ListGroup,
  ListGroupItem,
  Row,
  useScreenSize
} from 'straightline-ui';
import { formatMoney } from 'straightline-utils/accounting';
import { isLTL, MODE } from 'straightline-utils/constants';

const carrierLogos = {
  AACT: AACTimg,
  AVRT: AVRTimg,
  CCYQ: CCYQimg,
  DAYR: DAYRimg,
  FEDX: FEDXimg,
  FXFE: FXFEimg,
  FXNL: FXFEimg,
  FWDN: FWDNimg,
  HMES: HMESimg,
  MANI: MANIimg,
  MIDW: MIDWimg,
  NPME: NPMEimg,
  ODFL: ODFLimg,
  RDWY: RDWYimg,
  SAIA: SAIAimg,
  WARD: WARDimg,
  RETL: RETLimg,
  SUON: SUONimg,
  TFORCE: TFORCEimg,
  TFIN: TFORCEimg,
  UPGF: TFORCEimg,
  SEFL: SEFLimg,
  SFPA: SFPAimg,
  OAKH: OAKHimg,
  UPS: UPSimg,
  USPS: USPSimg
};

export const CarrierLogo = ({ carrier, size = 45 }) => {
  if (!carrier) {
    return (
      <div
        className="d-flex align-items-center justify-content-center mt-1"
        style={{ height: size, width: size }}
      >
        <img
          style={{ maxHeight: size, maxWidth: size }}
          src={faviconImg}
          alt="Carrier Logo"
        />
      </div>
    );
  }

  const logo = carrierLogos[carrier.scac_code];

  if (logo) {
    return (
      <div
        className="d-flex align-items-center justify-content-center mt-1"
        style={{ height: size, width: size }}
      >
        <img
          style={{ maxHeight: size, maxWidth: size }}
          src={carrierLogos[carrier.scac_code] || faviconImg}
          alt={`${carrier.name} Logo`}
        />
      </div>
    );
  }

  const parts = carrier.name.split(' ');
  const acronym = parts
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
    .replace(/[^0-9a-zA-Z]/g, '')
    .slice(0, 2);

  const fontSize = size / 2;

  return (
    <div
      className="d-flex align-items-center justify-content-center rounded font-weight-bold badge-soft-primary"
      style={{ height: size, width: size }}
    >
      <div style={{ fontSize }}>{acronym}</div>
    </div>
  );
};

function RatePrice({ value, rate, offer, action }) {
  const { md } = useScreenSize();

  return (
    <div className="d-flex align-items-center py-1 justify-content-end-sm">
      {!md && action && (
        <div className="mr-3">
          <Input
            type="radio"
            className="mr-n2"
            // bsSize="lg"
            checked={value === rate.id}
          />
        </div>
      )}
      <div
        className={value === rate.id ? 'text-primary' : 'text-secondary'}
        style={{ fontSize: 22 }}
      >
        {offer?.accepted_at ? (
          <div className="">
            <span className="mr-3">
              {formatMoney(offer.amount, {
                precision: 0
              })}
            </span>
            <del>
              {formatMoney(rate.price.shipper_price, {
                precision: 0
              })}
            </del>
          </div>
        ) : (
          <div className="">
            {formatMoney(rate.price.shipper_price, {
              precision: 0
            })}
          </div>
        )}
      </div>
      {md && action && (
        <div className="ml-3">
          <Input
            type="radio"
            className="mr-n2"
            // bsSize="lg"
            checked={value === rate.id}
          />
        </div>
      )}
    </div>
  );
}

export default function RatePicker({
  disabled,
  name,
  value,
  onChange,
  quote,
  action = true
}) {
  const { md } = useScreenSize();
  const { rates, offers } = quote;
  const sortedRates = useMemo(() => {
    return [...rates].sort((rate1, rate2) => {
      return rate1.price.shipper_price - rate2.price.shipper_price;
    });
  }, [rates]);

  return (
    <ListGroup>
      {sortedRates.map((rate) => {
        const offer = offers?.find((offer) => offer.rate_id === rate.id);

        let insuranceAmount = null;
        if (rate.insurance) {
          insuranceAmount =
            (rate.insurance.premium_amount || 0) +
            (rate.insurance.tax_amount || 0) +
            (rate.insurance.service_fee_amount || 0);
        }

        const isAmazon = quote.delivery_address.name
          ?.toLowerCase()
          .includes('amazon');

        const isAmazonCarrier =
          isLTL(quote) &&
          ['SAIA', 'FEDX', 'FXNL', 'FXFE'].includes(rate.carrier?.scac_code);

        return (
          <ListGroupItem
            key={rate.id}
            action={action}
            tag={action ? 'button' : 'li'}
            type={action ? 'button' : ''}
            disabled={disabled}
            // active={values.rate_id === rate.id}
            // className={value === rate.id ? 'bg-lighter' : ''}
            style={{
              backgroundColor: value === rate.id ? '#e0edff' : ''
            }}
            onClick={() => {
              if (action) {
                onChange({
                  target: {
                    rate,
                    name,
                    value: rate.id
                  }
                });
              }
            }}
          >
            <Row>
              <Col xs="auto" className="pr-0">
                <CarrierLogo carrier={rate.carrier} size={md ? 45 : 35} />
              </Col>
              <Col>
                <Row className="d-flex justify-content-between align-items-start">
                  <Col>
                    <div className="font-weight-bold">
                      {rate.carrier?.name || 'Straightline'}
                    </div>
                    {/* <div>{rate.carrier_rate_id || 'NA'}</div> */}
                    <div className="font-weight-bold">
                      {rate.service_level.name}
                    </div>
                  </Col>
                  <Col sm="auto">
                    {md && (
                      <RatePrice
                        value={value}
                        rate={rate}
                        offer={offer}
                        action={action}
                      />
                    )}
                  </Col>
                </Row>
                <div>
                  <span>{rate.service_level.description}</span>
                  <CostMetric rate={rate} quote={quote} disabled={disabled} />
                </div>
                {isLTL(quote) && rate.service_level.lane === 'INTERLINE' && (
                  <div className="text-secondary">
                    *This is an interline rate and may be transported by partner
                    carriers.
                  </div>
                )}
                {isLTL(quote) && rate.service_level.lane === 'UNSPECIFIED' && (
                  <div className="text-secondary">
                    *The lane for this rate is unspecified and may be
                    transported by partner carriers.
                  </div>
                )}
                {insuranceAmount && (
                  <div>
                    <span className="text-secondary">
                      There will be an additional {formatMoney(insuranceAmount)}{' '}
                      insurance charge.
                    </span>
                  </div>
                )}
                {isAmazon && isAmazonCarrier && (
                  <div className="text-danger small">
                    {rate.carrier?.name} has known problems delivering to Amazon
                    distribution centers. Please contact Straightline before
                    booking this rate.
                  </div>
                )}
                {offer && (
                  <div>
                    {offer.accepted_at ? (
                      <div className="mt-2 text-primary font-weight-bold">
                        Your offer has been accepted! You can now book this rate
                        for{' '}
                        <span className="font-weight-bold">
                          {formatMoney(offer.amount)}
                        </span>
                        .
                      </div>
                    ) : (
                      <div className="mt-2 text-info font-weight-bold">
                        Your offer of{' '}
                        <span className="font-weight-bold">
                          {formatMoney(offer.amount)}
                        </span>{' '}
                        is being reviewed. You will be notified when it is
                        accepted or denied.
                      </div>
                    )}
                  </div>
                )}
                {!md && (
                  <RatePrice
                    value={value}
                    rate={rate}
                    offer={offer}
                    action={action}
                  />
                )}
              </Col>
            </Row>
          </ListGroupItem>
        );
      })}
    </ListGroup>
  );
}

function CostMetric({ rate, quote, disabled }) {
  const { travel, load_weight, line_items } = quote;
  const color = disabled ? 'text-muted' : 'text-secondary';

  if (rate.mode === MODE.PARCEL) {
    return (
      <span>
        <span className={`${color} ml-2 mr-2`}>&bull;</span>
        {formatMoney(rate.price.shipper_price / load_weight)}
        <span className={`small ${color}`}>lb</span>
      </span>
    );
  }

  const units = line_items.reduce((sum, item) => sum + item.units, 0);

  return (
    <span>
      <span className={`${color} ml-2 mr-2`}>&bull;</span>
      <span>{formatMoney(rate.price.shipper_price / travel.miles)}</span>
      <span className={`small ${color} mr-2`}>mi</span>
      <span className={`${color} mr-2`}>&bull;</span>
      <span>{formatMoney(rate.price.shipper_price / load_weight)}</span>
      <span className={`small ${color} mr-2`}>lb</span>
      <span className={`${color} mr-2`}>&bull;</span>
      <span>{formatMoney(rate.price.shipper_price / units)}</span>
      <span className={`small ${color}`}>plt</span>
    </span>
  );
}
